import type { CityCode } from '@loci/city-editions';

import type { HrefSurface } from '@/lib/app-surface-href';
import { appUrl, cityUrl, type CrossHostStageOptions } from '@/lib/cross-host-urls';

export interface DiscoverHrefOptions extends CrossHostStageOptions {
  readonly cityCode: CityCode;
  /** Host surface that renders the link (`useCitySurface()` / `getCitySurface()`). */
  readonly surface: HrefSurface;
  /** The viewer carries a session cookie (`useCityViewerHasSession()`). */
  readonly hasSession: boolean;
  /** Discover query, without the leading `?`. Empty or omitted for none. */
  readonly query?: string;
}

/**
 * Single source of truth for links INTO Discover (WEB-1231, ruling R25:
 * "signed-in users stay on `app.*` for every discovery hop").
 *
 * | rendered on      | signed in                       | anonymous                     |
 * | ---------------- | ------------------------------- | ----------------------------- |
 * | `app`            | `/<city>/discover` (relative)   | same (app host is sign-in only) |
 * | `marketing-city` | `appUrl(<city>, '/discover')`   | `/discover` (relative)        |
 * | `marketing-apex` | `appUrl(<city>, '/discover')`   | `cityUrl(<city>, '/discover')`|
 * | `fallback`       | `/<city>/discover` (relative)   | `/discover` (relative)        |
 *
 * Root-relative `/discover` on the app host is a 404 (bare, city-less app
 * path), and root-relative `/<city>/discover` on a city host is RSC-prefetched,
 * 307'd cross-host by the proxy and blocked by `connect-src` (WEB-834). So a
 * link only stays relative when the destination is on the host that renders
 * it; a cross-host destination is absolute, and call sites render it as a
 * plain `<a>` (see {@link import('@/lib/app-surface-href').isCrossHostHref}).
 */
export function buildDiscoverHref({
  cityCode,
  surface,
  hasSession,
  query,
  ...stage
}: DiscoverHrefOptions): string {
  const suffix = query ? `?${query}` : '';
  const appRelative = `/${cityCode.toLowerCase()}/discover${suffix}`;
  const cityRelative = `/discover${suffix}`;

  switch (surface) {
    case 'app':
      return appRelative;
    case 'fallback':
      return hasSession ? appRelative : cityRelative;
    case 'marketing-city':
      return hasSession
        ? appUrl({ city: cityCode, path: `/discover${suffix}`, ...stage })
        : cityRelative;
    case 'marketing-apex':
      return hasSession
        ? appUrl({ city: cityCode, path: `/discover${suffix}`, ...stage })
        : cityUrl(cityCode, `/discover${suffix}`, stage);
  }
}

/**
 * The Discover query carried by a city-relative `/discover[?…]` href, or
 * `undefined` for any other href. Lets a renderer whose copy module emits
 * plain city-host hrefs route just the Discover-bound ones through
 * {@link buildDiscoverHref} (WEB-1231 / R25).
 */
export function discoverQueryOf(href: string): string | undefined {
  if (href === '/discover') return '';
  if (href.startsWith('/discover?')) return href.slice('/discover?'.length);
  return undefined;
}
